import React, { useState } from 'react';
import moment from 'moment';

const CampaignChecklist = ({ campaigns, onUpdate, loading }) => {
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    status: '',
    notes: '',
    action_taken: '',
  });

  const actionOptions = [
    { value: 'BUDGET_ADJUSTMENT', label: 'Budget Adjustment' },
    { value: 'AUDIENCE_OPTIMIZATION', label: 'Audience Optimization' },
    { value: 'AD_CREATIVE_UPDATE', label: 'Ad Creative Update' },
    { value: 'CAMPAIGN_PAUSED', label: 'Campaign Paused' },
    { value: 'KEYWORD_ADJUSTMENT', label: 'Keyword Adjustment' },
    { value: 'OTHER', label: 'Other' },
  ];

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign._id);
    setFormData({
      status: campaign.status,
      notes: campaign.notes || '',
      action_taken: campaign.action_taken || '',
    });
  };

  const handleSave = async (campaignId) => {
    try {
      await onUpdate(
        campaignId,
        formData.status,
        formData.notes,
        formData.action_taken
      );
      setEditingCampaign(null);
      setFormData({ status: '', notes: '', action_taken: '' });
    } catch (error) {
      console.error('Error updating campaign:', error);
    }
  };

  const handleCancel = () => {
    setEditingCampaign(null);
    setFormData({ status: '', notes: '', action_taken: '' });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'fas fa-check-circle text-success';
      case 'SKIPPED':
        return 'fas fa-times-circle text-warning';
      default:
        return 'fas fa-circle text-muted';
    }
  };

  const getIssueTypeColor = (issueType) => {
    switch (issueType) {
      case 'CPL_EXCEEDED':
        return 'danger';
      case 'NO_LEADS':
        return 'warning';
      case 'LOW_PERFORMANCE':
        return 'info';
      default:
        return 'secondary';
    }
  };

  if (!campaigns || campaigns.length === 0) {
    return (
      <div className='text-center py-4'>
        <i className='fas fa-tasks fa-2x text-muted mb-3'></i>
        <p className='text-muted'>No campaigns found</p>
      </div>
    );
  }

  return (
    <div className='campaign-checklist'>
      {campaigns.map((campaign, index) => (
        <div
          key={campaign._id || index}
          className='campaign-item mb-3 border rounded p-3'
        >
          <div className='d-flex align-items-center justify-content-between'>
            <div className='d-flex align-items-center flex-grow-1'>
              <div className='me-3'>
                <i className={getStatusIcon(campaign.status)}></i>
              </div>

              <div className='flex-grow-1'>
                <div className='d-flex align-items-center mb-1'>
                  <h6 className='mb-0 me-2'>{campaign.campaign_name}</h6>
                  <span
                    className={`badge bg-${getIssueTypeColor(
                      campaign.issue_type
                    )} me-2`}
                  >
                    {campaign.issue_type === 'CPL_EXCEEDED'
                      ? 'CPL Exceeded'
                      : campaign.issue_type === 'NO_LEADS'
                      ? 'No Leads'
                      : 'Low Performance'}
                  </span>
                  <small className='text-muted'>
                    ID: {campaign.campaign_id}
                  </small>
                </div>

                <div className='d-flex gap-3 text-sm'>
                  <span>
                    <strong>CPL:</strong>
                    <span
                      className={
                        campaign.current_cpl > campaign.benchmark
                          ? 'text-danger'
                          : 'text-success'
                      }
                    >
                      {campaign.current_cpl
                        ? `$${campaign.current_cpl}`
                        : 'No Leads'}
                    </span>
                  </span>
                  <span>
                    <strong>Benchmark:</strong> ${campaign.benchmark}
                  </span>
                  <span>
                    <strong>Spend:</strong> ${campaign.spend}
                  </span>
                  <span>
                    <strong>Conversions:</strong> {campaign.conversions}
                  </span>
                </div>
              </div>
            </div>

            <div className='d-flex gap-2'>
              <button
                className='btn btn-sm btn-outline-primary'
                onClick={() =>
                  setExpandedCampaign(
                    expandedCampaign === campaign._id ? null : campaign._id
                  )
                }
              >
                <i
                  className={`fas fa-chevron-${
                    expandedCampaign === campaign._id ? 'up' : 'down'
                  }`}
                ></i>
              </button>

              {/* Always allow editing - removed completed status restriction */}
              <button
                className='btn btn-sm btn-outline-success'
                onClick={() => handleEdit(campaign)}
                disabled={loading}
              >
                <i className='fas fa-edit'></i>
              </button>
            </div>
          </div>

          {/* Expanded Details */}
          {expandedCampaign === campaign._id && (
            <div className='mt-3 pt-3 border-top'>
              {campaign.notes && (
                <div className='mb-3'>
                  <strong>Notes:</strong>
                  <div className='bg-light p-2 rounded mt-1'>
                    {campaign.notes}
                  </div>
                </div>
              )}

              {campaign.action_taken && (
                <div className='mb-3'>
                  <strong>Action Taken:</strong>
                  <span className='ms-2 badge bg-info'>
                    {actionOptions.find(
                      (opt) => opt.value === campaign.action_taken
                    )?.label || campaign.action_taken}
                  </span>
                </div>
              )}

              {campaign.completed_at && (
                <div className='mb-3'>
                  <strong>Completed:</strong>
                  <span className='ms-2 text-muted'>
                    {moment(campaign.completed_at).format('MMM DD, YYYY HH:mm')}
                  </span>
                </div>
              )}

              {/* Display comments (read-only) */}
              {campaign.comments && campaign.comments.length > 0 && (
                <div className='mb-3'>
                  <strong>Comments ({campaign.comments.length})</strong>
                  <div className='campaign-comments mt-2'>
                    {campaign.comments
                      .slice()
                      .reverse()
                      .map((comment, index) => (
                        <div
                          key={comment._id || index}
                          className='comment-item mb-2 p-2 bg-light rounded border'
                        >
                          <div className='d-flex justify-content-between align-items-start mb-1'>
                            <small className='fw-bold text-primary'>
                              {comment.created_by?.name || 'Unknown User'}
                            </small>
                            <small className='text-muted'>
                              {moment(comment.created_at).format(
                                'MMM DD, HH:mm'
                              )}
                            </small>
                          </div>
                          <div className='comment-text'>{comment.comment}</div>
                          <small className='text-muted'>
                            {moment(comment.created_at).fromNow()}
                          </small>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Edit Form */}
          {editingCampaign === campaign._id && (
            <div className='mt-3 pt-3 border-top'>
              <div className='row g-3'>
                <div className='col-md-4'>
                  <label className='form-label'>Status</label>
                  <select
                    className='form-select form-select-sm'
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                  >
                    <option value='PENDING'>Pending</option>
                    <option value='COMPLETED'>Completed</option>
                    <option value='SKIPPED'>Skipped</option>
                  </select>
                </div>

                <div className='col-md-4'>
                  <label className='form-label'>Action Taken</label>
                  <select
                    className='form-select form-select-sm'
                    value={formData.action_taken}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        action_taken: e.target.value,
                      }))
                    }
                  >
                    <option value=''>Select action...</option>
                    {actionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className='col-12'>
                  <label className='form-label'>Notes</label>
                  <textarea
                    className='form-control form-control-sm'
                    rows='2'
                    placeholder='Add notes about what you did...'
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                  ></textarea>
                </div>

                <div className='col-12'>
                  <div className='d-flex gap-2'>
                    <button
                      className='btn btn-sm btn-success'
                      onClick={() => handleSave(campaign._id)}
                      disabled={loading}
                    >
                      {loading ? (
                        <i className='fas fa-spinner fa-spin me-1'></i>
                      ) : (
                        <i className='fas fa-save me-1'></i>
                      )}
                      Save
                    </button>
                    <button
                      className='btn btn-sm btn-outline-secondary'
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CampaignChecklist;
